import { AxiosResponse } from 'axios';
import PropTypes from 'prop-types';
import { Action } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ConfigBase } from '../../types/config';

import { TyreContactInputDTO } from '../../types/dto/TyreContactInputDTO';
import { TyreDTO } from '../../types/dto/TyreDTO';
import { TyreLOVDTO } from '../../types/dto/TyreLOVDTO';
import { Utils } from '../../utils';
import ApiHelper from '../ApiHelper';
import { AuthState } from '../auth/Types';
import * as Actions from './Actions';
import { TyreState } from './Types';

type RootSubState = {
    authState: AuthState,
    tyreState: TyreState,
};

type CommonAppThunkAction<R> = ThunkAction<R, RootSubState, undefined, Action>;
type CommonAppThunkDispatch = ThunkDispatch<RootSubState, undefined, Action>;

export type ServiceActions = {
    getTyreSeccao: () => Promise<TyreLOVDTO[]>,
    getTyreSerie: (seccao: string) => Promise<TyreLOVDTO[]>,
    getTyreJante: (seccao: string, serie: string) => Promise<TyreLOVDTO[]>,
    getTyreList: (seccao: string, serie: string, jante: string) => Promise<TyreDTO[]>,
    tyreContact: (data: TyreContactInputDTO) => Promise<void>,
};

export const ServiceActionPropTypes = PropTypes.shape({
    getTyreSeccao: PropTypes.func.isRequired,
    getTyreSerie: PropTypes.func.isRequired,
    getTyreJante: PropTypes.func.isRequired,
    getTyreList: PropTypes.func.isRequired,
    tyreContact: PropTypes.func.isRequired,
});

export const createServiceActions = (config: ConfigBase) => {
    const apiHelper = ApiHelper(config);

    const getTyreSeccao = (): CommonAppThunkAction<Promise<TyreLOVDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getTyreSeccaoRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/tyre/getTyreSeccao/`, null));
                const response = axiosResponse.data as TyreLOVDTO[];
                dispatch(Actions.getTyreSeccaoSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getTyreSeccaoFailure());
                throw error;
            }
        };
    };
    const getTyreSerie = (seccao: string): CommonAppThunkAction<Promise<TyreLOVDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getTyreSerieRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/tyre/getTyreSerie/?seccao=${seccao}`, null));
                const response = axiosResponse.data as TyreLOVDTO[];
                dispatch(Actions.getTyreSerieSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getTyreSerieFailure());
                throw error;
            }
        };
    };
    const getTyreJante = (seccao: string, serie: string): CommonAppThunkAction<Promise<TyreLOVDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getTyreJanteRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/tyre/getTyreJante/?seccao=${seccao}&serie=${serie}`, null));
                const response = axiosResponse.data as TyreLOVDTO[];
                dispatch(Actions.getTyreJanteSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getTyreJanteFailure());
                throw error;
            }
        };
    };
    const getTyreList = (seccao: string, serie: string, jante: string): CommonAppThunkAction<Promise<TyreDTO[]>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.getTyreListRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.get(`/api/${config.ResourceCode}/tyre/getTyreList/?seccao=${seccao}&serie=${serie}&jante=${jante}`, null));
                const response = axiosResponse.data as TyreDTO[];
                dispatch(Actions.getTyreListSuccess(response));
                return response;
            } catch (error) {
                dispatch(Actions.getTyreListFailure());
                throw error;
            }
        };
    };

    const tyreContact = (data: TyreContactInputDTO): CommonAppThunkAction<Promise<void>> => {
        return async (dispatch) => {
            try {
                dispatch(Actions.tyreContactRequest());
                const axiosResponse: AxiosResponse = await dispatch(apiHelper.post(`/api/${config.ResourceCode}/tyre/tyrecontact`, data));
                Utils.checkHttpStatus(axiosResponse);
                dispatch(Actions.tyreContactSuccess());
            } catch (error) {
                dispatch(Actions.tyreContactFailure());
                throw error;
            }
        };
    };

    return {
        getTyreSeccao,
        getTyreSerie,
        getTyreJante,
        getTyreList,
        tyreContact,
    };
};

export const bindServiceActionCreators = (config: ConfigBase, dispatch: CommonAppThunkDispatch): ServiceActions => {
    const services = createServiceActions(config);
    return {
        getTyreSeccao: () => dispatch(services.getTyreSeccao()),
        getTyreSerie: (seccao: string) => dispatch(services.getTyreSerie(seccao)),
        getTyreJante: (seccao: string, serie: string) => dispatch(services.getTyreJante(seccao, serie)),
        getTyreList: (seccao: string, serie: string, jante: string) => dispatch(services.getTyreList(seccao, serie, jante)),
        tyreContact: (data: TyreContactInputDTO) => dispatch(services.tyreContact(data)),
    };
};
